import { EmployeeIdFormV2 } from '../EmployeeIdFormV2';
import EnterCustomIDEmailForm from '../EnterCustomIDEmailForm';
import EnterPasswordForm from '../EnterPasswordForm';
import EnterWorkDetailsForm from '../EnterWorkDetailsForm';
import EnterWorkEmailForm from '../EnterWorkEmailForm';
import NameForm from '../NameForm';
import PrivacyConsentForm from '../PrivacyConsentForm';
import { ComponentProps, RegistrationFlowState } from './types';

export enum AccountVerificationSubRoutes {
  EmployeeId = 'employeeId',
  Email = 'email',
  WorkEmail = 'workEmail',
  Confirmation = 'confirmation',
}

export enum AccountCreationSubRoutes {
  Name = 'name',
  WorkDetails = 'work-details',
  PrivacyConsent = 'privacy-consent',
  Password = 'password',
}

export type SubRoutes = AccountVerificationSubRoutes | AccountCreationSubRoutes;

export enum RegistrationMethod {
  OPEN_EMAIL = 'OPEN_EMAIL',
  CLOSED_EMPLOYEE_ID = 'CLOSED_EMPLOYEE_ID',
  SSO = 'SSO',
}

export enum RegistrationFlow {
  VERIFICATION_CLOSED_EMPLOYEE_ID = 'verification-closed-employee-id',
  VERIFICATION_OPEN_EMAIL = 'verification-open-email',
  ACCOUNT_CREATION = 'account-creation',
}

/**
 * Defines the required fields and default values for each registration flow type.
 */
export const SignUpFlowValidationRules: {
  [K in RegistrationFlow]: {
    requiredFields: (keyof RegistrationFlowState[K])[]; // Fields required for validation
    defaultValues: RegistrationFlowState[K]; // Default values for the state
  };
} = {
  [RegistrationFlow.VERIFICATION_CLOSED_EMPLOYEE_ID]: {
    requiredFields: ['employeeId', 'email'],
    defaultValues: {
      employeeId: '',
      email: '',
    },
  },
  [RegistrationFlow.VERIFICATION_OPEN_EMAIL]: {
    requiredFields: ['email'],
    defaultValues: {
      email: '',
    },
  },
  [RegistrationFlow.ACCOUNT_CREATION]: {
    requiredFields: [
      'firstName',
      'lastName',
      'password',
      'explicitPrivacyConsentGiven',
      'marketingOptIn',
      'explicitEEADataStorageConsentGiven',
    ],
    defaultValues: {
      firstName: '',
      lastName: '',
      location: '',
      department: '',
      password: '',
      explicitPrivacyConsentGiven: false,
      marketingOptIn: false,
      explicitEEADataStorageConsentGiven: false,
    },
  },
};

const EmployeeIdFormComponent = {
  route: AccountVerificationSubRoutes.EmployeeId,
  component: EmployeeIdFormV2,
  fields: ['employeeId'],
};

const EmailFormComponent = {
  route: AccountVerificationSubRoutes.Email,
  component: EnterCustomIDEmailForm,
  fields: ['email'],
};

const WorkEmailFormComponent = {
  route: AccountVerificationSubRoutes.WorkEmail,
  component: EnterWorkEmailForm,
  fields: ['email'],
};

const NameFormComponent = {
  route: AccountCreationSubRoutes.Name,
  component: NameForm,
  fields: ['firstName', 'lastName'],
};

const WorkDetailsFormComponent = {
  route: AccountCreationSubRoutes.WorkDetails,
  component: EnterWorkDetailsForm,
  fields: ['location', 'department'],
};

const PrivacyConsentFormComponent = {
  route: AccountCreationSubRoutes.PrivacyConsent,
  component: PrivacyConsentForm,
  fields: [
    'explicitPrivacyConsentGiven',
    'marketingOptIn',
    'explicitEEADataStorageConsentGiven',
  ],
};

const PasswordFormComponent = {
  route: AccountCreationSubRoutes.Password,
  component: EnterPasswordForm,
  fields: ['password'],
};

/**
 * Defines the order of sub routes for each registraion flow and components to render at each route.
 */
export const RegistrationMethodMap: {
  [K in RegistrationFlow]: {
    route: SubRoutes;
    component: React.ComponentType<ComponentProps<RegistrationFlowState[K]>>;
    fields: string[];
  }[];
} = {
  [RegistrationFlow.VERIFICATION_CLOSED_EMPLOYEE_ID]: [
    EmployeeIdFormComponent,
    EmailFormComponent,
  ],
  [RegistrationFlow.VERIFICATION_OPEN_EMAIL]: [WorkEmailFormComponent],
  [RegistrationFlow.ACCOUNT_CREATION]: [
    NameFormComponent,
    WorkDetailsFormComponent,
    PrivacyConsentFormComponent,
    PasswordFormComponent,
  ],
};
